/**
 * Default Quick Actions for CommandPalette
 *
 * @module command-palette/actions
 * @version 1.0
 * @updated 2026-04-27
 *
 * @description
 * Acciones rápidas locales: no tocan backend ni AddressSearch.
 * Usa Zustand stores directamente en onExecute (fuera de hooks React) para
 * evitar prop drilling. Edit lock se evalúa desde deps capturadas en useMemo.
 *
 * @pending
 * - Fase 2D: absorción de AddressSearch / Nominatim
 * - Fase 2E: acciones de módulo dinámicas (exportar, análisis zonal, etc.)
 */

import type { CommandAction, CommandActionGroup } from '../types';
import type { ModuleId } from '../../module-store/types';
import type { OperationalMode } from '../../operational-modes/types';
import { useModuleStore } from '../../module-store';
import { useOperationalModeStore } from '../../operational-modes';

// ─── Deps ──────────────────────────────────────────────────────────────────────

export interface DefaultActionsDeps {
  /** Estado de edición activa (de useEditLock) */
  isEditingActive: boolean;
  hasUnsavedChanges: boolean;
  /** Módulos disponibles para el proyecto (de useModuleStore) */
  availableModules: ModuleId[];
  /** Módulo activo actualmente (de useModuleStore) — permite toggle correcto */
  activeModule: ModuleId | null;
  /** Abre el DataHubModal — callback desde TopBar */
  onOpenDataHub?: () => void;
  /** Abre/cierra el ChatPanel — callback desde TopBar */
  onToggleChat?: () => void;
}

// ─── SVG Icons ─────────────────────────────────────────────────────────────────

function IconDatabase(): JSX.Element {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
      <ellipse cx="12" cy="5" rx="9" ry="3" />
      <path d="M21 12c0 1.657-4.03 3-9 3s-9-1.343-9-3" />
      <path d="M3 5v14c0 1.657 4.03 3 9 3s9-1.343 9-3V5" />
    </svg>
  );
}

function IconChat(): JSX.Element {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
      <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
    </svg>
  );
}

function IconModule(): JSX.Element {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="3" width="7" height="7" rx="1" />
      <rect x="14" y="3" width="7" height="7" rx="1" />
      <rect x="3" y="14" width="7" height="7" rx="1" />
      <rect x="14" y="14" width="7" height="7" rx="1" />
    </svg>
  );
}

function IconModeInitial({ initial }: { initial: string }): JSX.Element {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5}>
      <circle cx="12" cy="12" r="9" />
      <text
        x="12" y="17"
        textAnchor="middle"
        fontSize="12"
        fontWeight="700"
        fill="currentColor"
        stroke="none"
        fontFamily="'Sora', 'Inter', sans-serif"
      >
        {initial}
      </text>
    </svg>
  );
}

// ─── Module definitions ─────────────────────────────────────────────────────────

interface ModuleActionDef {
  id: ModuleId;
  label: string;
  description: string;
  keywords: string[];
}

const MODULE_ACTION_DEFS: ModuleActionDef[] = [
  { id: 'urban-network', label: 'Red Urbana',     description: 'Red vial canónica y topología',          keywords: ['red', 'vial', 'calles', 'topología', 'network', 'urban'] },
  { id: 'waste',         label: 'Residuos',        description: 'Contenedores y recolección',             keywords: ['residuos', 'contenedores', 'basura', 'recolección', 'waste'] },
  { id: 'mobility',      label: 'Movilidad',       description: 'Paradas, rutas y ciclovías',             keywords: ['movilidad', 'paradas', 'rutas', 'ciclovías', 'mobility'] },
  { id: 'environmental', label: 'Ambiental',       description: 'Arbolado y espacios verdes',             keywords: ['ambiental', 'arbolado', 'árboles', 'verde', 'parques', 'environmental'] },
  { id: 'transport',     label: 'Transporte',      description: 'Transporte público, rutas y flota',      keywords: ['transporte', 'bus', 'colectivo', 'flota', 'transport'] },
  { id: 'roadways',      label: 'Vialidades',      description: 'Segmentos de calzada y defectos',        keywords: ['vialidades', 'calzada', 'defectos', 'pavimento', 'roads'] },
  { id: 'infrastructure',label: 'Infraestructura', description: 'Alumbrado y mobiliario urbano',          keywords: ['infraestructura', 'alumbrado', 'mobiliario', 'infrastructure'] },
  { id: 'transit',       label: 'Tránsito',        description: 'Semáforos y señalización vial',          keywords: ['tránsito', 'semáforos', 'señalización', 'transit'] },
  { id: 'safety',        label: 'Seguridad',       description: 'Cámaras y monitoreo',                    keywords: ['seguridad', 'cámaras', 'emergencias', 'safety'] },
  { id: 'urban',         label: 'Planificación',   description: 'Zonificación y código urbano',           keywords: ['planificación', 'zonificación', 'edificios', 'urban', 'plan'] },
  { id: 'social',        label: 'Social',          description: 'Escuelas, hospitales y equipamiento',    keywords: ['social', 'escuelas', 'hospitales', 'equipamiento'] },
  { id: 'services',      label: 'Servicios',       description: 'Agua, cloacas, gas y electricidad',      keywords: ['servicios', 'agua', 'cloacas', 'electricidad', 'services'] },
];

// ─── Mode definitions ───────────────────────────────────────────────────────────

interface ModeActionDef {
  id: OperationalMode;
  label: string;
  description: string;
  keywords: string[];
  initial: string;
}

const MODE_ACTION_DEFS: ModeActionDef[] = [
  { id: 'explore',   label: 'Modo Explorar',    description: 'Navegar y consultar el mapa',         keywords: ['explorar', 'navegar', 'ver', 'consultar', 'explore',   'modo e'], initial: 'E' },
  { id: 'inventory', label: 'Modo Inventariar', description: 'Crear y editar elementos del catastro', keywords: ['inventariar', 'crear', 'editar', 'catastro',           'modo i'], initial: 'I' },
  { id: 'analyze',   label: 'Modo Analizar',    description: 'Análisis de indicadores y zonas',      keywords: ['analizar', 'análisis', 'indicadores', 'métricas',     'modo a'], initial: 'A' },
  { id: 'plan',      label: 'Modo Planificar',  description: 'Diseñar intervenciones y escenarios',  keywords: ['planificar', 'escenarios', 'diseñar', 'intervenciones', 'modo p'], initial: 'P' },
  { id: 'operate',   label: 'Modo Operar',      description: 'Gestión operativa y seguimiento',      keywords: ['operar', 'operativo', 'gestión', 'seguimiento',       'modo o'], initial: 'O' },
];

// ─── Factory ────────────────────────────────────────────────────────────────────

/**
 * Construye la lista de acciones rápidas para CommandPalette.
 *
 * Llamar desde `useMemo` dentro del componente para mantener deps frescos:
 * ```tsx
 * const allActions = useMemo(
 *   () => buildDefaultActions({ isEditingActive, hasUnsavedChanges, availableModules, onOpenDataHub, onToggleChat }),
 *   [isEditingActive, hasUnsavedChanges, availableModules, onOpenDataHub, onToggleChat]
 * );
 * ```
 */
export function buildDefaultActions(deps: DefaultActionsDeps): CommandAction[] {
  const { isEditingActive, hasUnsavedChanges, availableModules, activeModule, onOpenDataHub, onToggleChat } = deps;
  const editLocked = isEditingActive && hasUnsavedChanges;
  const editLockedReason = 'Guardá o cancelá los cambios antes de continuar';

  const actions: CommandAction[] = [];

  // ── ACCIONES ──────────────────────────────────────────────────────────────

  if (onOpenDataHub) {
    actions.push({
      id: 'action:open-data-hub',
      label: 'Gestionar datos',
      description: 'Importar y exportar información del proyecto',
      keywords: ['datos', 'importar', 'exportar', 'data', 'hub', 'gestionar', 'csv', 'archivo'],
      icon: <IconDatabase />,
      group: 'actions',
      onExecute: onOpenDataHub,
    });
  }

  if (onToggleChat) {
    actions.push({
      id: 'action:toggle-chat',
      label: 'Chat IA',
      description: 'Abrir o cerrar el asistente de inteligencia artificial',
      keywords: ['chat', 'ia', 'asistente', 'ai', 'inteligencia', 'artificial', 'copilot'],
      icon: <IconChat />,
      group: 'actions',
      onExecute: onToggleChat,
    });
  }

  // ── MÓDULOS ───────────────────────────────────────────────────────────────

  for (const def of MODULE_ACTION_DEFS) {
    const isAvailable = availableModules.includes(def.id);
    // Usar activeModule de las deps (fresco, no stale) para el toggle correcto
    const isActive = activeModule === def.id;

    actions.push({
      id: `module:${def.id}`,
      label: def.label,
      description: def.description,
      keywords: def.keywords,
      icon: <IconModule />,
      group: 'modules',
      disabled: !isAvailable || editLocked,
      disabledReason: !isAvailable
        ? 'Módulo no disponible en tu plan'
        : editLocked ? editLockedReason : undefined,
      onExecute: () => {
        if (!isAvailable) return;
        if (isEditingActive && hasUnsavedChanges) {
          alert('Guardá o cancelá los cambios antes de cambiar de módulo.');
          return;
        }
        // Toggle: si ya está activo, desactivar (null)
        const next = isActive ? null : def.id;
        useModuleStore.getState().setActiveModule(next);
      },
    });
  }

  // ── MODOS ─────────────────────────────────────────────────────────────────

  for (const def of MODE_ACTION_DEFS) {
    actions.push({
      id: `mode:${def.id}`,
      label: def.label,
      description: def.description,
      keywords: def.keywords,
      icon: <IconModeInitial initial={def.initial} />,
      group: 'modes',
      disabled: editLocked,
      disabledReason: editLocked ? editLockedReason : undefined,
      onExecute: () => {
        if (isEditingActive && hasUnsavedChanges) {
          alert('Guardá o cancelá los cambios antes de cambiar de modo.');
          return;
        }
        useOperationalModeStore.getState().setMode(def.id);
      },
    });
  }

  return actions;
}

// ─── Utils ──────────────────────────────────────────────────────────────────────

/**
 * Filtra acciones por query (label, description, keywords).
 * Con query vacía o < 1 char, devuelve todas las acciones.
 */
export function filterActions(actions: CommandAction[], query: string): CommandAction[] {
  const q = query.trim().toLowerCase();
  if (!q) return actions;
  return actions.filter(a =>
    a.label.toLowerCase().includes(q) ||
    a.description?.toLowerCase().includes(q) ||
    a.keywords.some(k => k.toLowerCase().includes(q))
  );
}

/**
 * Agrupa acciones por su campo group.
 * Preserva el orden canónico: actions → modules → modes.
 */
export function groupActions(
  actions: CommandAction[]
): Map<CommandActionGroup, CommandAction[]> {
  const order: CommandActionGroup[] = ['actions', 'modules', 'modes'];
  const map = new Map<CommandActionGroup, CommandAction[]>(
    order.map(key => [key, []])
  );
  for (const action of actions) {
    map.get(action.group)!.push(action);
  }
  return map;
}
